/**
 * Los tres idiomas del lanzamiento (D17) y el tipo `Texto`, que es un registro
 * TOTAL sobre ellos.
 *
 * POR QUÉ TOTAL Y NO `Partial`: la cicatriz nº 35 de OJOS. Allí un mapa de
 * traducciones se indexaba por `string` y tenía cuatro entradas para once
 * códigos, y un usuario acabó leyendo `motor.sistema_sin_ejercicio` en su
 * rutina. Aquí el mismo fallo no sería feo: sería una palabra prohibida por el
 * MDR impresa en la pantalla de un producto comercial (`01 §4 bis`).
 *
 * Con `Record<Idioma, string>` añadir un idioma rompe la compilación en cada
 * texto que falte, antes de que exista la pantalla.
 */

export const IDIOMAS = ["es", "en", "pt"] as const;

export type Idioma = (typeof IDIOMAS)[number];

/** Un texto publicable. Los tres idiomas o no compila. */
export type Texto = Readonly<Record<Idioma, string>>;

/**
 * El idioma por defecto cuando aún no se sabe cuál quiere la persona.
 *
 * **INGLÉS, y es una decisión del dueño del 07/09/2026** (opción A del
 * `PLANNER-DEL-REDISENO §18.7`). Antes era `es`, y eso significaba que un
 * alemán con el navegador en alemán abría la página de venta en castellano:
 * el idioma que menos probabilidades tiene de entender de los tres.
 *
 * El primer escalón NO cambia: manda lo que la persona ha puesto en su
 * navegador, porque eso lo ha elegido ella y un brasileño en Miami sigue
 * queriendo portugués. Esto es solo el último escalón, el de «no reconozco
 * ninguno de los tres».
 *
 * Y NO se mira el país: para saberlo hay que mirar la IP, que es un dato
 * personal, y hoy la landing no guarda nada de quien entra — que es lo que
 * permite no tener cartel de cookies.
 *
 * Lo fija `idiomas.prueba.ts` por sus dos caras: que sin cabecera sale `en`,
 * y que con una cabecera reconocible NO sale `en`. Sin la segunda, devolver
 * siempre `en` pasaría en verde.
 */
export const IDIOMA_POR_DEFECTO: Idioma = "en";

/**
 * Las etiquetas largas de cada idioma, para el selector. `es` es español
 * NEUTRO deliberadamente: no es de España ni de un país concreto, porque el
 * mercado es todo el continente.
 */
export const NOMBRE_DEL_IDIOMA: Readonly<Record<Idioma, string>> = {
  es: "Español",
  en: "English (US)",
  pt: "Português (BR)",
};

/** La etiqueta BCP‑47 que va en `<html lang>` y en la cabecera HTTP. */
export const ETIQUETA_BCP47: Readonly<Record<Idioma, string>> = {
  es: "es",
  en: "en-US",
  pt: "pt-BR",
};

export function esIdioma(valor: unknown): valor is Idioma {
  return typeof valor === "string" && (IDIOMAS as readonly string[]).includes(valor);
}

/**
 * Negocia el idioma a partir de la cabecera `Accept-Language`. Devuelve el
 * por defecto si no reconoce ninguno: nunca falla, nunca devuelve `undefined`.
 */
export function negociarIdioma(cabecera: string | undefined | null): Idioma {
  if (!cabecera) return IDIOMA_POR_DEFECTO;
  const preferencias = cabecera
    .split(",")
    .map((parte) => {
      const [etiqueta = "", ...resto] = parte.trim().split(";");
      const q = resto
        .map((r) => r.trim())
        .find((r) => r.startsWith("q="))
        ?.slice(2);
      return { etiqueta: etiqueta.toLowerCase(), peso: q === undefined ? 1 : Number(q) };
    })
    .filter((p) => Number.isFinite(p.peso) && p.peso > 0)
    .sort((a, b) => b.peso - a.peso);

  for (const { etiqueta } of preferencias) {
    const base = etiqueta.split("-")[0];
    if (esIdioma(base)) return base;
  }
  return IDIOMA_POR_DEFECTO;
}
